import { useState, useCallback } from "react";
import { AVATARS, type AvatarId } from "@jumpbean/shared";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { Avatar } from "./Avatar";
import { apiPost } from "../lib/api";

export interface AddChildModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** Callback when a child is successfully created */
  onSuccess: (child: { id: string; displayName: string; avatarId: string; dateOfBirth: string | null }) => void;
  /** Whether the form is submitting */
  isLoading?: boolean;
}

/**
 * Modal for creating a new child profile.
 * Features a name input and colorful avatar selection grid.
 *
 * @example
 * <AddChildModal
 *   isOpen={showAddChild}
 *   onClose={() => setShowAddChild(false)}
 *   onSuccess={(child) => console.log('Created:', child)}
 * />
 */
export function AddChildModal({
  isOpen,
  onClose,
  onSuccess,
  isLoading = false,
}: AddChildModalProps) {
  const [displayName, setDisplayName] = useState("");
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarId | null>(null);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setDisplayName("");
      setSelectedAvatar(null);
      setDateOfBirth("");
      setError(null);
      onClose();
    }
  }, [isSubmitting, onClose]);

  const handleSubmit = async () => {
    // Validation
    const trimmedName = displayName.trim();
    if (!trimmedName) {
      setError("Please enter a name");
      return;
    }
    if (trimmedName.length > 50) {
      setError("Name is too long");
      return;
    }
    if (!selectedAvatar) {
      setError("Please choose an avatar");
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const { data, error: apiError } = await apiPost<{ id: string; displayName: string; avatarId: string; dateOfBirth: string | null }>(
        "/api/children",
        {
          displayName: trimmedName,
          avatarId: selectedAvatar,
          ...(dateOfBirth && { dateOfBirth }),
        }
      );

      if (apiError || !data) {
        if (apiError?.status === 403) {
          setError(apiError.details as string || "You've reached your child profile limit");
        } else {
          setError(apiError?.message || "Failed to create profile");
        }
        return;
      }

      // Success - reset form and notify parent
      setDisplayName("");
      setSelectedAvatar(null);
      setDateOfBirth("");
      onSuccess({
        id: data.id,
        displayName: data.displayName,
        avatarId: data.avatarId,
        dateOfBirth: data.dateOfBirth,
      });
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = displayName.trim().length > 0 && selectedAvatar !== null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add a New Friend!"
      size="md"
      closeOnBackdropClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <div className="flex flex-col gap-6">
        {/* Name Input */}
        <div className="space-y-2">
          <label
            htmlFor="child-name"
            className="block font-display font-semibold text-body-lg text-neutral-700"
          >
            What's their name?
          </label>
          <input
            id="child-name"
            type="text"
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              if (error) setError(null);
            }}
            placeholder="Enter name..."
            maxLength={50}
            disabled={isSubmitting}
            className="
              w-full
              px-5 py-4
              font-body text-body-lg text-neutral-800
              bg-neutral-50
              border-[3px] border-neutral-200
              rounded-2xl
              placeholder:text-neutral-400
              transition-all duration-200
              focus:outline-none focus:border-magic-400 focus:ring-4 focus:ring-magic-100
              disabled:opacity-60 disabled:cursor-not-allowed
            "
          />
        </div>

        {/* Date of Birth Input */}
        <div className="space-y-2">
          <label
            htmlFor="child-dob"
            className="block font-display font-semibold text-body-lg text-neutral-700"
          >
            When's their birthday?
          </label>
          <input
            id="child-dob"
            type="date"
            value={dateOfBirth}
            onChange={(e) => {
              setDateOfBirth(e.target.value);
              if (error) setError(null);
            }}
            max={new Date().toISOString().split("T")[0]}
            disabled={isSubmitting}
            className="
              w-full
              px-5 py-4
              font-body text-body-lg text-neutral-800
              bg-neutral-50
              border-[3px] border-neutral-200
              rounded-2xl
              transition-all duration-200
              focus:outline-none focus:border-magic-400 focus:ring-4 focus:ring-magic-100
              disabled:opacity-60 disabled:cursor-not-allowed
            "
          />
          <p className="font-body text-body-sm text-neutral-500">
            This helps us tailor content to their age
          </p>
        </div>

        {/* Avatar Selection */}
        <div className="space-y-3">
          <p className="font-display font-semibold text-body-lg text-neutral-700">
            Choose their avatar
          </p>
          <div className="grid grid-cols-4 gap-3">
            {AVATARS.map((avatar: typeof AVATARS[number]) => (
              <Avatar
                key={avatar.id}
                avatarId={avatar.id}
                size="lg"
                isSelectable
                isSelected={selectedAvatar === avatar.id}
                onSelect={() => {
                  setSelectedAvatar(avatar.id);
                  if (error) setError(null);
                }}
                className="mx-auto"
              />
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="
              px-4 py-3
              bg-fairy-50
              border-2 border-fairy-300
              rounded-xl
              text-center
            "
            role="alert"
          >
            <p className="font-body text-body-md text-fairy-700 font-medium">
              {error}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 pt-2">
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleSubmit}
            isLoading={isSubmitting || isLoading}
            disabled={!isFormValid || isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create Profile"}
          </Button>

          <Button
            variant="ghost"
            size="md"
            fullWidth
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default AddChildModal;
